import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import PageTitle from "../../components/Page Component/PageTitle";
import StarRating from "../../components/StarRating";
import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../store/actions";
import { FEEDBACK_REQUESTS_API } from "../../Service/Apis";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single rating criterion from the template, used to render labelled star rows. */
interface Parameter {
  parameter_id: string;
  label: string;
  help_text: string;
  /** Determines the display order of parameters, sorted ascending before rendering. */
  order: number;
}

/**
 * The client's submitted ratings and comments.
 * `parameter_ratings` is a map of parameter_id → numeric score; missing ids indicate
 * the client skipped a non-mandatory parameter.
 */
interface FeedbackResponse {
  parameter_ratings: Record<string, number>;
  overall_rating: number;
  additional_comments: string;
  submitted_at: string;
}

/**
 * Snapshot of the template configuration embedded in the API response.
 * This is stored alongside the response so that the display remains accurate
 * even if the original template is later edited or deactivated.
 */
interface TemplateDetail {
  rating_scale: number;
  /** When true, the client section renders "Anonymous" instead of the client's name and email. */
  is_anonymous: boolean;
  parameters: Parameter[];
}

/**
 * Full detail payload for a feedback request, including the embedded response
 * (if submitted) and a snapshot of the template used.
 */
interface FeedbackDetail {
  request_id: string;
  employee_name: string;
  project_name: string | null;
  client_name: string;
  client_email: string;
  template_name: string;
  is_submitted: boolean;
  created_at: string;
  expires_at: string;
  /** Null until the client submits the form. */
  response: FeedbackResponse | null;
  /** Null if the template was hard-deleted; the view degrades gracefully in that case. */
  template_detail: TemplateDetail | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Read-only detail page that displays the full response for a submitted client
 * feedback request. Accessed by internal staff (Admin / HR / Manager) via the
 * feedback request list.
 *
 * The page resolves the request ID from the URL params and fetches its detail.
 * If the request has not yet been submitted, a placeholder message is shown
 * rather than an empty ratings section.
 */
const ViewCustomerFeedback: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<FeedbackDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await dispatch(fetchData(`${FEEDBACK_REQUESTS_API}${id}/`));
        setDetail(data);
      } catch {
        setError("Failed to load feedback details.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, dispatch]);

  /**
   * Formats an ISO timestamp as a localised date-time string including the hour
   * and minute, e.g. "23 Apr 2026, 14:05". Used specifically for the "Submitted at"
   * timestamp where time precision matters. Returns an em-dash for empty values.
   */
  const formatDate = (iso: string): string => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Container maxWidth="md" sx={{ mt: 12 }}>
        <Typography color="error">{error || "Feedback not found."}</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/feedback/requests")}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Container>
    );
  }

  const resp = detail.response;
  const tmplDetail = detail.template_detail;
  const parameters: Parameter[] = tmplDetail?.parameters ?? [];
  const ratingScale: number = tmplDetail?.rating_scale ?? 5;
  const isAnonymous: boolean = tmplDetail?.is_anonymous ?? false;

  return (
    <Container maxWidth="md" sx={{ mt: 12, mb: 10 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/feedback/requests")}
        sx={{ mb: 2 }}
      >
        Back to List
      </Button>

      <PageTitle>Client Feedback</PageTitle>

      <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
        {/* Meta info */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Employee
            </Typography>
            <Typography fontWeight={600}>{detail.employee_name}</Typography>
          </Box>
          {detail.project_name && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Project
              </Typography>
              <Typography fontWeight={600}>{detail.project_name}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Template
            </Typography>
            <Typography fontWeight={600}>{detail.template_name}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <Chip
                label={detail.is_submitted ? "Submitted" : "Pending"}
                color={detail.is_submitted ? "success" : "warning"}
                size="small"
              />
            </Box>
          </Box>
        </Box>

        {/* Client identity */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Client
          </Typography>
          {isAnonymous ? (
            <Typography fontStyle="italic" color="text.secondary">
              Anonymous
            </Typography>
          ) : (
            <Typography fontWeight={600}>
              {detail.client_name}{" "}
              <Typography component="span" color="text.secondary">
                &lt;{detail.client_email}&gt;
              </Typography>
            </Typography>
          )}
        </Box>

        {resp && (
          <>
            <Divider sx={{ mb: 3 }} />

            {/* Submitted at */}
            <Typography variant="caption" color="text.secondary">
              Submitted at: {formatDate(resp.submitted_at)}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Parameter ratings */}
            {parameters.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Parameter Ratings
                </Typography>
                {[...parameters]
                  .sort((a, b) => a.order - b.order)
                  .map((param) => {
                    const rating = resp.parameter_ratings[param.parameter_id] ?? 0;
                    return (
                      <Box
                        key={param.parameter_id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1.5,
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography variant="body2" fontWeight={500} sx={{ minWidth: 180 }}>
                          {param.label}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <StarRating
                            scale={ratingScale}
                            value={Number(rating)}
                            readOnly
                            size={22}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 40 }}
                          >
                            {rating > 0 ? `${rating} / ${ratingScale}` : "—"}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Overall rating */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Overall Rating
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <StarRating
                  scale={ratingScale}
                  value={Number(resp.overall_rating)}
                  readOnly
                  size={28}
                />
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  {resp.overall_rating} / {ratingScale}
                </Typography>
              </Box>
            </Box>

            {/* Comments */}
            {resp.additional_comments && (
              <>
                <Divider sx={{ mb: 3 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Additional Comments
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, backgroundColor: "grey.50" }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {resp.additional_comments}
                    </Typography>
                  </Paper>
                </Box>
              </>
            )}
          </>
        )}

        {!detail.is_submitted && (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              This feedback has not been submitted yet.
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ViewCustomerFeedback;
